import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import VideoPlayer from '../components/VideoPlayer';
import { ArrowLeft, Tv, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WatchPage() {
  const { episodeId } = useParams();
  const { user } = useAuth();
  const [episode, setEpisode] = useState(null);
  const [show, setShow] = useState(null);
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/episodes/${episodeId}`)
      .then(async (r) => {
        setEpisode(r.data);
        const [showRes, epsRes] = await Promise.all([
          axios.get(`${API}/shows/${r.data.show_id}`),
          axios.get(`${API}/shows/${r.data.show_id}/episodes`),
        ]);
        setShow(showRes.data);
        setAllEpisodes(epsRes.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [episodeId]);

  // Record watch history
  useEffect(() => {
    if (user && episode) {
      axios.post(`${API}/watch-history`, {
        episode_id: episode.id,
        show_id: episode.show_id,
        progress: 10
      }, { withCredentials: true }).catch(() => {});
    }
  }, [user, episode]);

  if (loading) return (
    <div className="page-content flex items-center justify-center min-h-[60vh]">
      <div className="animate-pulse"><div className="h-8 bg-gray-200 rounded w-48" /></div>
    </div>
  );

  if (!episode) return (
    <div className="page-content flex items-center justify-center min-h-[60vh]">
      <p className="font-['Fredoka'] text-2xl text-[#4B5563]">Episode not found</p>
    </div>
  );

  const currentIndex = allEpisodes.findIndex(e => e.id === episodeId);
  const nextEpisode = currentIndex >= 0 && currentIndex < allEpisodes.length - 1 ? allEpisodes[currentIndex + 1] : null;

  return (
    <div className="page-content bg-[#0A0A0A] min-h-screen">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        {/* Back */}
        {show && (
          <Link to={`/show/${show.id}`} className="inline-flex items-center gap-1 text-sm font-bold text-[#4AC6FF] hover:underline mb-4" data-testid="back-to-show">
            <ArrowLeft size={16} strokeWidth={2.5} /> Back to {show.title}
          </Link>
        )}

        {/* Video Player */}
        <VideoPlayer videoId={episode.youtube_video_id} title={episode.title} showScanlines={false} />

        {/* Episode Info */}
        <div className="mt-6 p-5 rounded-xl border-2 border-white/10 bg-white/5" data-testid="episode-info">
          <h1 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-white mb-2">
            {episode.title}
          </h1>
          {show && (
            <Link to={`/show/${show.id}`} className="inline-flex items-center gap-1 text-sm text-[#4AC6FF] font-bold hover:underline mb-3">
              <Tv size={14} strokeWidth={2.5} /> {show.title} - S{episode.season}E{episode.episode_number}
            </Link>
          )}
          {episode.description && (
            <p className="text-sm text-white/70 font-['Nunito']">{episode.description}</p>
          )}
        </div>

        {/* Next Episode */}
        {nextEpisode && (
          <Link
            to={`/watch/${nextEpisode.id}`}
            className="mt-4 flex items-center justify-between p-4 rounded-xl border-2 border-white/10 bg-white/5 hover:bg-white/10 transition-colors group"
            data-testid="next-episode-link"
          >
            <div>
              <span className="text-xs text-white/50 font-['IBM_Plex_Mono'] uppercase tracking-wider">Up Next</span>
              <p className="text-white font-['Fredoka'] font-bold">S{nextEpisode.season}E{nextEpisode.episode_number} - {nextEpisode.title}</p>
            </div>
            <ChevronRight size={20} className="text-white/50 group-hover:text-white transition-colors" strokeWidth={2.5} />
          </Link>
        )}

        {/* Episode List */}
        {allEpisodes.length > 1 && (
          <div className="mt-8">
            <h2 className="font-['Fredoka'] text-xl font-bold text-white mb-4">All Episodes</h2>
            <div className="space-y-2">
              {allEpisodes.map(ep => (
                <Link
                  key={ep.id}
                  to={`/watch/${ep.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                    ep.id === episodeId
                      ? 'border-[#FF3860] bg-[#FF3860]/10 text-white'
                      : 'border-white/10 bg-white/5 text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`episode-list-${ep.id}`}
                >
                  <span className="text-xs font-['IBM_Plex_Mono'] w-12 shrink-0">S{ep.season}E{ep.episode_number}</span>
                  <span className="font-['Nunito'] font-bold truncate">{ep.title}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
